import { useCallback } from 'react';

import { useLocale } from './use-locale';
import type { Locale } from './locale';

interface Named {
  readonly nameAr?: string | null;
  readonly nameEn?: string | null;
}

/**
 * The name of a catalogue row (a plan, a role, a specialty) in the admin's language.
 *
 * The API stores both `nameAr` and `nameEn`. When the one asked for is blank — rows
 * seeded before the English column was filled in — the other is shown instead: an
 * Arabic plan name on an English screen is a translation gap, a blank cell is a bug.
 */
export function localizedName(locale: Locale, row: Named): string {
  const preferred = locale === 'ar' ? row.nameAr : row.nameEn;
  const other = locale === 'ar' ? row.nameEn : row.nameAr;
  return preferred?.trim() || other?.trim() || '';
}

/** `const name = useLocalizedName(); name(plan)` */
export function useLocalizedName(): (row: Named) => string {
  const { locale } = useLocale();
  return useCallback((row: Named) => localizedName(locale, row), [locale]);
}
